/**
 * BSC (FOR-011): espejo TS de lo que T3 necesita previsualizar de
 * BscCalculadora y BscMetaParser (docs/BSC.md §3 y §7). SOLO para pintar en
 * vivo mientras se escribe: el servidor manda. Lo que diga esta previa no
 * bloquea nada; enviar a autorización lo revisa BscPlaneacionBuss con las
 * reglas del ciclo y contesta sus faltantes.
 *
 * Misma escala que el motor (6 decimales, toFixed(6)) para que la suma que
 * se ve antes de guardar sea la que dirá el servidor después. F6.4 agrega el
 * ponderado (K) para el cuadro de captura de C1 y A3; C3 (F6.5) lo reutiliza
 * y agrega el ratio, el semáforo y el alcance del área en el trimestre
 * (BscCalculadora.ratio, semaforo y calcularArea) para el pie del paso 2.
 * Las barras de acumulado de A2 y C2 y la barrita de los tiles toman de aquí
 * su lugar en la pista (escalaBarras, enPista), que pasa de 100 %.
 *
 * Funciones puras, sin Angular.
 */

import { SemaforoBsc } from "src/app/shared/model/bsc/bsc.model";

/** BscCalculadora.ESCALA. */
const ESCALA = 6;
/** meta_valor DECIMAL(14,4): BscPlaneacionBuss redondea la meta a 4. */
const ESCALA_META = 4;

/** Los valores de omisión del ciclo (BscCicloBean.conDefaults). */
export const EXIGIR_SUMA_POR_OMISION = true;
export const TOLERANCIA_POR_OMISION = 0.005;
export const VERDE_POR_OMISION = 0.95;
export const AMARILLO_POR_OMISION = 0.8;
export const EXCLUIR_NA_POR_OMISION = true;

/** Lo que el motor diría de los pesos de un KPI (BscCalculadora.validarPesos y calcularKpi). */
export interface RevisionPesos {
  /** Suma de los pesos de los trimestres que aplican, fracción a 6 decimales. */
  suma: number;
  /** Primer trimestre con aplica = true; nulo si no aplica en ninguno. */
  trimestreInicio: number | null;
  /**
   * VAL-BSC-PESOS bloquea el envío (aplica desde Q1, o en ningún trimestre, o un
   * peso negativo); PESOS_PARCIALES solo avisa (el KPI inicia después de Q1).
   * Nulo si cuadran o el ciclo no exige la suma.
   */
  codigo: "VAL-BSC-PESOS" | "PESOS_PARCIALES" | null;
  /** Trimestres NA que traen peso (PESO_EN_TRIMESTRE_NA: cuenta en lo programado). */
  pesoEnNa: number[];
}

/** Redondeo HALF_UP a `escala` decimales, sin el ruido del punto flotante. */
export function redondear(valor: number, escala = ESCALA): number {
  const factor = 10 ** escala;
  const r = Math.round((Math.abs(valor) + Number.EPSILON) * factor) / factor;
  return valor < 0 ? -r : r;
}

/**
 * BscCalculadora.validarPesos: suma los pesos de los trimestres que aplican;
 * con `exigirSuma` y |suma − 1| > tolerancia, el KPI que aplica desde Q1 (o en
 * ninguno) da VAL-BSC-PESOS y el que inicia después PESOS_PARCIALES. Un peso
 * negativo en un trimestre que aplica es VAL-BSC-PESOS siempre. La igualdad
 * con la tolerancia pasa.
 *
 * @param aplica 4 posiciones; false = NA
 * @param pesos  4 fracciones 0..1; nulo = 0
 */
export function revisarPesos(
  aplica: boolean[],
  pesos: (number | null)[],
  exigirSuma = EXIGIR_SUMA_POR_OMISION,
  tolerancia = TOLERANCIA_POR_OMISION,
): RevisionPesos {
  let suma = 0;
  let negativo = false;
  let inicio: number | null = null;
  const pesoEnNa: number[] = [];
  for (let i = 0; i < 4; i++) {
    const m = redondear(Number(pesos[i] ?? 0) || 0);
    if (aplica[i]) {
      inicio ??= i + 1;
      negativo = negativo || m < 0;
      suma = redondear(suma + m);
    } else if (m !== 0) {
      pesoEnNa.push(i + 1);
    }
  }
  let codigo: RevisionPesos["codigo"] = null;
  if (negativo) {
    codigo = "VAL-BSC-PESOS";
  } else if (exigirSuma && redondear(Math.abs(suma - 1)) > redondear(tolerancia)) {
    codigo = inicio != null && inicio > 1 ? "PESOS_PARCIALES" : "VAL-BSC-PESOS";
  }
  return { suma, trimestreInicio: inicio, codigo, pesoEnNa };
}

/**
 * K del FOR-011 (BscCalculadora.calcularKpi): real × peso / programado, a 6
 * decimales. 0 si el programado es 0 o negativo (el aviso PROGRAMADO_CERO del
 * motor); nulo sin real. F6.4: la previa del cuadro de captura (C1/A3).
 *
 * @param real       I, fracción
 * @param peso       M, fracción; nulo = 0
 * @param programado J, fracción; nulo = 1 (nace en 1)
 */
export function ponderado(real: number | null, peso: number | null, programado: number | null): number | null {
  if (real == null) {
    return null;
  }
  const j = programado == null ? 1 : redondear(programado);
  if (j <= 0) {
    return 0;
  }
  return redondear((redondear(real) * redondear(peso ?? 0)) / j);
}

/**
 * BscCalculadora.ratio: real / programado a 6 decimales; nulo si falta uno o
 * el programado es 0 (el semáforo sale SIN_PROGRAMA). F6.5: L/N de un KPI
 * (bajo meta si es menor al verde, la regla del cierre) y A/AP del área.
 */
export function ratio(real: number | null, programado: number | null): number | null {
  if (real == null || programado == null || redondear(programado) === 0) {
    return null;
  }
  return redondear(redondear(real) / redondear(programado));
}

/**
 * BscCalculadora.semaforo: VERDE si el ratio llega al verde, AMARILLO si
 * llega al amarillo, si no ROJO; sin ratio, SIN_PROGRAMA. No resuelve
 * INICIA_Qn (eso es del KPI). Los umbrales nulos toman los de omisión.
 */
export function semaforo(r: number | null, verde: number | null = null, amarillo: number | null = null): SemaforoBsc {
  if (r == null) {
    return "SIN_PROGRAMA";
  }
  if (r >= redondear(verde ?? VERDE_POR_OMISION)) {
    return "VERDE";
  }
  return r >= redondear(amarillo ?? AMARILLO_POR_OMISION) ? "AMARILLO" : "ROJO";
}

/** Un KPI ACTIVO en el promedio del área de un trimestre: su L, su N y si aplica en él. */
export interface AcumuladoKpi {
  /** L del trimestre, fracción. */
  acumReal: number;
  /** N del trimestre, fracción. */
  acumProg: number;
  aplica: boolean;
}

/**
 * BscCalculadora.calcularArea en un trimestre: A = promedio de las L y AP =
 * promedio de las N de los KPI que cuentan; con `excluirNa` (DEFAULT del
 * ciclo) el que no aplica no entra ni al numerador ni al denominador, sin él
 * entra con su L (literal del Excel). Sin KPIs que cuenten, los dos nulos.
 */
export function alcanceArea(
  kpis: AcumuladoKpi[],
  excluirNa = EXCLUIR_NA_POR_OMISION,
): { real: number | null; programado: number | null } {
  let sumaL = 0;
  let sumaN = 0;
  let cuantos = 0;
  for (const k of kpis) {
    if (excluirNa && !k.aplica) {
      continue;
    }
    sumaL = redondear(sumaL + redondear(k.acumReal));
    sumaN = redondear(sumaN + redondear(k.acumProg));
    cuantos++;
  }
  if (cuantos === 0) {
    return { real: null, programado: null };
  }
  return { real: redondear(sumaL / cuantos), programado: redondear(sumaN / cuantos) };
}

/** Una barra de avance en fracción (1 = 100 %): el real y lo programado (nulo = sin raya). */
export interface FraccionesBarra {
  real: number;
  programado: number | null;
}

/** Dónde se pinta una barra en su pista, 0..100: el ancho del real y la raya de lo programado. */
export interface PistaBarra {
  ancho: number;
  raya: number | null;
}

/**
 * Hasta dónde llega la escala común de las barras: 200 %. Un sobrecumplimiento
 * como los del FOR-011 (109 %, 152 %) se ve en proporción; un valor atípico
 * (50000 % en No o días, que el servidor guarda con aviso) llena su pista, con
 * su texto al lado, en vez de dejar sin ancho las barras de los demás KPIs.
 */
const ESCALA_MAXIMA_BARRAS = 2;

/**
 * La escala común de las barras de una tabla, como en barras-area-bsc y
 * acumulado-q-bsc: el mayor real o programado de las que se ven, nunca menos
 * de 1 ni más de ESCALA_MAXIMA_BARRAS. Con un 109 % la pista llega a 109 % y
 * un 100 % se ve más corto; si nada pasa de 100 %, la escala es 1 y las barras
 * salen como siempre.
 */
export function escalaBarras(barras: readonly FraccionesBarra[]): number {
  const mayor = Math.max(1, ...barras.map((b) => Math.max(b.real, b.programado ?? 0)));
  return Math.min(ESCALA_MAXIMA_BARRAS, mayor);
}

/**
 * La barra en la pista con la escala de su tabla (escalaBarras): un negativo
 * queda en 0 y lo que pasa de la escala llena la pista.
 */
export function enPista(barra: FraccionesBarra, escala: number): PistaBarra {
  const lugar = (fraccion: number) => Math.min(100, Math.max(0, (fraccion / escala) * 100));
  return { ancho: lugar(barra.real), raya: barra.programado == null ? null : lugar(barra.programado) };
}

/**
 * BscMetaParser.PATRON_META: número, % opcional y el resto como texto. Las
 * búsquedas hacia adelante («que no siga un espacio», «que no siga un dígito»)
 * hacen que cada tramo tome completo lo suyo: lee lo mismo, sin el retroceso
 * cuadrático cuando el resto trae un salto de renglón.
 */
const PATRON_META = /^\s*(?!\s)(\d+(?!\d)(?:[.,]\d+(?!\d))?)\s*(?!\s)(%?)\s*(?!\s)(.*)$/;

/**
 * La meta que guardaría BscPlaneacionBuss con lo que se escribió y la unidad
 * del combo: "95%" → 0.95; "95" con % → 0.95 (de 1 a 100 se lee como
 * porcentaje); "0,95" → 0.95; "6 productos" → 6; "1,000" (¿coma decimal o de
 * miles?) y lo que no empieza con número → nulo (se guarda como texto).
 * A 4 decimales, como meta_valor.
 */
export function metaDeTexto(texto: string | null | undefined, unidad: string | null | undefined): number | null {
  const t = (texto ?? "").replace(/[\u00A0\u202F]/g, " ").trim();
  const m = PATRON_META.exec(t);
  if (!t || !m) {
    return null;
  }
  const coma = m[1].indexOf(",");
  if (coma > 0 && m[1].length - coma - 1 === 3 && Number(m[1].slice(0, coma)) !== 0) {
    return null;
  }
  const numero = Number(m[1].replace(",", "."));
  if (!Number.isFinite(numero)) {
    return null;
  }
  let valor = m[2] ? redondear(numero / 100) : redondear(numero);
  const soloNumero = !m[2] && m[3].trim() === "";
  if ((unidad ?? "%") === "%" && soloNumero && valor > 1 && valor <= 100) {
    valor = redondear(valor / 100);
  }
  return redondear(valor, ESCALA_META);
}

/**
 * La unidad que el texto de la meta dice por sí mismo (BscMetaParser):
 * "95%" → %, "12 días" → dias, "6 productos" → No (lo que se cuenta), "3 No"
 * → No. Nula si el texto no la dice ("95", "0.95") o no empieza con número:
 * entonces manda el combo. T3 la usa para mover el combo al escribir, porque
 * en el servidor el combo gana y "3 defectos" con % se guardaría como 300 %.
 */
export function unidadDeMeta(texto: string | null | undefined): string | null {
  const t = (texto ?? "").replace(/[\u00A0\u202F]/g, " ").trim();
  const m = PATRON_META.exec(t);
  if (!t || !m) {
    return null;
  }
  if (m[2]) {
    return "%";
  }
  const resto = m[3]
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  if (!resto) {
    return null;
  }
  return unidadConocida(resto) ?? unidadConocida(resto.split(/\s+/)[0]) ?? "No";
}

/** La unidad que nombra una palabra de la meta ("%", "no", "dia", "dias"); nula si no es ninguna. */
function unidadConocida(x: string): string | null {
  switch (x) {
    case "%":
      return "%";
    case "no":
      return "No";
    case "dia":
    case "dias":
      return "dias";
    default:
      return null;
  }
}

/** La meta de un KPI como la compara el envío: valor, texto y unidad (nula = %). */
export interface MetaComparable {
  valor: number | null | undefined;
  texto: string | null | undefined;
  unidad: string | null | undefined;
}

/**
 * BscPlaneacionBuss.metaDistinta: otra unidad, otro valor, o los dos sin
 * valor y otro texto. Con el mismo valor y la misma unidad, otra redacción
 * ("6 productos" → "6 apps") no es un cambio de meta.
 */
export function metaDistinta(antes: MetaComparable, ahora: MetaComparable): boolean {
  if ((antes.unidad || "%") !== (ahora.unidad || "%")) {
    return true;
  }
  const a = antes.valor == null ? null : Number(antes.valor);
  const b = ahora.valor == null ? null : Number(ahora.valor);
  if (a != null || b != null) {
    return a == null || b == null || redondear(a, ESCALA_META) !== redondear(b, ESCALA_META);
  }
  const texto = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
  return texto(antes.texto) !== texto(ahora.texto);
}

/**
 * BscPlaneacionBuss.pesosDistintos: algún peso (nulo = 0) o alguna marca NA
 * (nula = aplica) distinta.
 */
export function pesosDistintos(
  aplicaAntes: boolean[],
  pesosAntes: (number | null)[],
  aplicaAhora: boolean[],
  pesosAhora: (number | null)[],
): boolean {
  for (let i = 0; i < 4; i++) {
    if (aplicaAntes[i] !== aplicaAhora[i]) {
      return true;
    }
    if (redondear(Number(pesosAntes[i] ?? 0) || 0) !== redondear(Number(pesosAhora[i] ?? 0) || 0)) {
      return true;
    }
  }
  return false;
}
